import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Portal } from '../ui/Portal';

interface AccountSwitcherProps {
  isExpanded: boolean;
}

export const AccountSwitcher: React.FC<AccountSwitcherProps> = ({ isExpanded }) => {
  const { t } = useTranslation();
  const { account, availableAccounts, switchAccount } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasMultipleAccounts = availableAccounts && availableAccounts.length > 1;

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const sidebarWidth = isExpanded ? 256 : 64; // w-64 = 16rem = 256px, w-16 = 4rem = 64px
      setDropdownStyle({
        top: rect.top,
        left: sidebarWidth,
        width: Math.max(200, rect.width)
      });
    }
  }, [isExpanded]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        dropdownRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!account) return null;

  return (
    <div className="border-b border-[#0044CC]">
      <button
        ref={buttonRef}
        onClick={() => {
          if (hasMultipleAccounts) {
            if (!isOpen) {
              updatePosition();
            }
            setIsOpen(!isOpen);
          }
        }}
        className={`w-full flex items-center text-sm font-medium text-white/90 h-10 ${
          hasMultipleAccounts ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className={isExpanded ? 'w-16 flex justify-center flex-shrink-0' : 'w-full flex justify-center'}>
          <div className="p-2.5">
            <Building2 className="w-4 h-4" />
          </div>
        </div>
        {isExpanded && (
          <span className="truncate pr-3">{account}</span>
        )}
        {hasMultipleAccounts && isExpanded && (
          <ChevronDown className={`w-4 h-4 absolute right-3 transition-transform ${isOpen ? 'rotate-90' : '-rotate-90'}`} />
        )}
      </button>

      {isOpen && hasMultipleAccounts && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed z-[1000] bg-white shadow-lg border border-gray-200 py-1 min-w-[200px] max-w-[250px] ml-1 max-h-[calc(100vh-80px)] overflow-y-auto"
            style={{
              top: `${dropdownStyle.top}px`,
              left: `${dropdownStyle.left}px`,
              transform: 'translateX(0)'
            }}
          >
            {[...availableAccounts].sort().map((acc) => (
              <button
                key={acc}
                onClick={() => {
                  switchAccount(acc);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-1.5 text-sm text-left hover:bg-gray-50 truncate ${
                  acc === account ? 'text-[#0055FF] font-medium' : 'text-gray-700'
                }`}
              >
                {acc}
              </button>
            ))}
          </div>
        </Portal>
      )}
    </div>
  );
};